import { useMemo, useCallback } from "react";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { useUnifiedItemsPage } from "@/hooks/useUnifiedItemsPage";
import { useStatusData } from "@/hooks/useStatusData";
import { useFolderManagement } from "@/hooks/useFolderManagement";
import { useFolderStructureVisibility, FolderStructureType } from "@/contexts/FolderStructureVisibilityContext";
import { getAllSubfolderIds, getFolderPath } from "@/lib/stores/folderStore";
import { ItemType, ItemTypeConfig, PageHandlers, UnifiedItemsData } from "../types";

interface UseUnifiedItemsDataProps {
  itemType: ItemType;
  config: ItemTypeConfig;
  handlers: PageHandlers;
}

/**
 * Hook that aggregates all data-related functionality for the unified items container
 * This is a generic hook that uses page-provided handlers for type-specific operations
 */
export function useUnifiedItemsData({
  itemType,
  config,
  handlers,
}: UseUnifiedItemsDataProps): UnifiedItemsData & {
  allCards: CardData[];
  filteredCards: CardData[];
} {
  // Preload status data for status-based shadow colors
  useStatusData();

  // Map itemType to FolderStructureType
  const folderStructureType: FolderStructureType = useMemo(() => {
    switch (itemType) {
      case 'library': return 'library';
      case 'documents': return 'documents';
      case 'blueprints': return 'blueprints';
      case 'dialogs': return 'dialogs';
      case 'variables': return 'variables';
      default: return 'flows';
    }
  }, [itemType]);

  // Get folder selection from FolderStructureVisibilityContext (independent per type)
  const { getSelectedFolderForType } = useFolderStructureVisibility();
  const selectedFolderId = getSelectedFolderForType(folderStructureType);

  // Use folder management for item drop handling and folder data
  const { handleItemDrop, folders } = useFolderManagement({ itemType });

  // Use the existing unified items page hook for data and handlers
  const unifiedPage = useUnifiedItemsPage({ itemType, config });

  // Filter cards by selected folder
  const filteredCards = useMemo(() => {
    const allCards = unifiedPage.cards;
    
    // If no folder is selected (root view), show all items
    if (selectedFolderId === null) {
      return allCards;
    }
    
    // If a folder is selected, show items in that folder AND its subfolders
    if (!unifiedPage.selectedGame) return allCards;
    
    const folderIds = getAllSubfolderIds(unifiedPage.selectedGame.id, itemType, selectedFolderId);
    
    return allCards.filter((card: CardData) => folderIds.includes(card.folder as string || ''));
  }, [unifiedPage.cards, selectedFolderId, unifiedPage.selectedGame, itemType]);

  // Create folder path lookup function for displaying folder paths in table view
  const folderPathLookup = useCallback((folderId: string | null | undefined): string => {
    if (!folderId || !unifiedPage.selectedGame?.id) return '';
    return getFolderPath(unifiedPage.selectedGame.id, itemType, folderId);
  }, [unifiedPage.selectedGame?.id, itemType]);

  return {
    // Core data from unified page
    data: unifiedPage.data,
    isLoading: unifiedPage.isLoading,
    cards: filteredCards,
    allCards: unifiedPage.cards,
    filteredCards,
    selectedGame: unifiedPage.selectedGame,
    tags: unifiedPage.tags,
    glossaryBlueprints: unifiedPage.glossaryBlueprints,

    // Data view mode support
    availableSections: unifiedPage.availableSections,
    availableFields: unifiedPage.availableFields,
    templateLookup: unifiedPage.templateLookup,
    rawItems: unifiedPage.data,

    // State
    isEditMode: unifiedPage.isEditMode,
    setIsEditMode: unifiedPage.setIsEditMode,

    // Handlers
    handleDelete: unifiedPage.handleDelete,
    handleCardClick: unifiedPage.handleCardClick,
    handleAdd: unifiedPage.handleAdd,
    handlePageConfigChange: unifiedPage.handlePageConfigChange,

    // Config
    pageLayoutConfig: unifiedPage.pageLayoutConfig,

    // Refresh handler
    refreshData: unifiedPage.refreshData,

    // Real-time sync
    newItemCount: unifiedPage.newItemCount,
    clearNewItems: unifiedPage.clearNewItems,

    // Folder data
    folders,
    selectedFolderId,
    folderPathLookup,
  };
}
